import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { CATEGORIES, PORTFOLIO } from "../data/content";

export const Portfolio = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const items = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter);
  const step = (dir) =>
    setActive((cur) => (cur === null ? null : (cur + dir + items.length) % items.length));

  return (
    <section id="portfolio" data-testid="section-portfolio" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center mb-12">
          <Eyebrow index="03">Portfolio</Eyebrow>
          <h2 className="font-serif-ed font-normal text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-[0.08em] text-[#2A1F16]">
            THE LOOKS
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => {
                setFilter(c);
                setActive(null);
              }}
              className={`text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                filter === c
                  ? "bg-[#C9A25F] text-[#2A1F16] border-[#B8893B]"
                  : "border-[#E9DCC8] text-[#6B5A49] hover:border-[#B8893B] hover:text-[#2A1F16]"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="columns-2 lg:columns-3 gap-4 [column-fill:balance]">
          <AnimatePresence mode="popLayout">
            {items.map((p, i) => (
              <motion.figure
                layout
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="relative mb-4 break-inside-avoid overflow-hidden rounded-xl group cursor-pointer"
                onClick={() => setActive(i)}
                data-testid="portfolio-gallery-item"
              >
                <img
                  src={p.url}
                  alt={`${p.title} — ${p.category} makeup by Mrinalee`}
                  loading="lazy"
                  className="w-full object-cover group-hover:scale-[1.04] transition-transform duration-[1.1s] ease-out"
                />
                <figcaption className="absolute inset-0 bg-gradient-to-t from-[#231A12]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                  <div>
                    <p className="font-serif-ed text-lg text-[#2A1F16]">{p.title}</p>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#B8893B]">{p.category}</p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            data-testid="portfolio-lightbox-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[#2A1F16]/94 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="lightbox-close-button"
              aria-label="Close"
              className="absolute top-5 right-5 text-[#F7EFE3]/80 hover:text-[#F7EFE3] p-2"
              onClick={() => setActive(null)}
            >
              <X size={26} />
            </button>
            <button
              data-testid="lightbox-prev-button"
              aria-label="Previous look"
              className="absolute left-3 sm:left-8 text-[#F7EFE3]/70 hover:text-[#F7EFE3] p-2"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
            >
              <ChevronLeft size={34} />
            </button>
            <motion.div
              key={items[active].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[active].url}
                alt={`${items[active].title} — ${items[active].category} makeup by Mrinalee`}
                className="w-full max-h-[74vh] object-contain rounded-lg"
              />
              <div className="mt-5 text-center">
                <p className="font-serif-ed text-2xl text-[#F7EFE3]">{items[active].title}</p>
                <p className="mt-1 text-[15px] text-[#F7EFE3]/60">{items[active].description}</p>
              </div>
            </motion.div>
            <button
              data-testid="lightbox-next-button"
              aria-label="Next look"
              className="absolute right-3 sm:right-8 text-[#F7EFE3]/70 hover:text-[#F7EFE3] p-2"
              onClick={(e) => { e.stopPropagation(); step(1); }}
            >
              <ChevronRight size={34} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
